import { Location } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { RootDialogComponent } from '@dntlab/share-files-dialog';
import FileSystemItem from 'devextreme/file_management/file_system_item';
import { Subscription } from 'rxjs';
import { SessionService } from 'src/app/auth/session.service';
import { promiseWithCatch, unsubscribeAll } from 'src/app/common/app.helpers';
import AppValues from 'src/app/common/app.values';
import ProjectLinks from 'src/app/common/project.links';
import { FileHistoryItems, FileSharedEvent } from 'src/app/interfaces/file.interface';
import { FoldersResponseInner } from 'src/app/swagger-api';
import { FileDetailsService } from '../file-details.service';
import { FileManagerService } from '../file-manager.service';

@Component({
  selector: 'app-file-details',
  templateUrl: './file-details.component.html',
  styleUrls: ['./file-details.component.scss'],
  providers: [FileDetailsService, RootDialogComponent],
})
export class FileDetailsComponent implements OnInit, OnDestroy {
  public fileDetails: FoldersResponseInner = null;
  public fileTrustedUrl: SafeUrl = null;
  public isFileOwner: boolean;
  public fileHistory: FileHistoryItems[] = [];

  public readonly DATE_TIME_FORMAT_SHORT: string = AppValues.DATE_TIME_FORMAT_SHORT;
  public readonly LINK_FILES_MANAGER: string = ProjectLinks.LINK_FILES_MANAGER;

  private subscriptions$: Subscription[] = [];

  private filesDetailsService = inject(FileDetailsService);
  private fileManagerService = inject(FileManagerService);
  private route = inject(ActivatedRoute);
  private sanitizer = inject(DomSanitizer);
  private router = inject(Router);
  private sessionService = inject(SessionService);
  private root = inject(RootDialogComponent);
  private location = inject(Location);

  public get height(): number {
    return window.innerHeight / 1.3;
  }

  public ngOnInit(): void {
    this.subscribeOnRouteChanges();
    this.subscribeToFileManagerChanges();

    this.location.onUrlChange(() => {
      if (this.root && this.root.modalService) {
        this.root.modalService.dismissAll();
      }
    });
  }

  public ngOnDestroy(): void {
    unsubscribeAll(this.subscriptions$);
  }

  /**
   * @description Calls on share-file button click. Calls fileManagerService's
   *              shareFiles() method.
   */
  public shareFile(): void {
    this.fileManagerService.shareFiles([this.fileDetails]);
  }

  /**
   * @description Calls on unshare-file button click. Calls fileManagerService's
   *              deleteFile() method and navigates to the previous page.
   */
  public unshareFile(): void {
    this.fileManagerService.deleteFile(this.fileDetails.entity.entity_id);
    this.location.back();
  }

  /**
   * @description Subscribes to file manager's downloadLinkReceived event and adapts
   *              the received public_url to TrustResourceUrl.
   *              Subscribes to file manager's fileShared event and updates security list
   *              in case equal entity_ids.
   */
  private subscribeToFileManagerChanges(): void {
    this.subscriptions$.push(
      this.fileManagerService.onDownloadLinkReceived.subscribe((links: { url: string; item: FileSystemItem }[]) => {
        this.fileTrustedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(links[0].url);
      }),
    );

    this.subscriptions$.push(
      this.fileManagerService.fileShared.subscribe((event: FileSharedEvent[]) => {
        if (event && this.fileDetails.entity.entity_id === event[0].entity_id) {
          this.fileDetails.security = event[0].security;
        }
      }),
    );
  }

  /**
   * @description Subscribes to file history changes.
   */
  private subscribeToFileHistoryChanges(): void {
    this.subscriptions$.push(
      this.filesDetailsService.onFileHistoryChanged.subscribe(
        (items: FileHistoryItems[]) => (this.fileHistory = items),
      ),
    );
  }

  /**
   * @description Subscribes on route params change
   *              and inits fileDetails property.
   */
  private subscribeOnRouteChanges(): void {
    this.subscriptions$.push(this.route.params.subscribe(() => this.saveFileDetails()));
  }

  /**
   * @description Fetches fileDetails value from router extra state.
   *              If fileDetails is undefined, redirects to /files-manager page.
   *              Otherwise fetches file public link and history details.
   */
  private saveFileDetails(): void {
    this.fileDetails = this.router.getCurrentNavigation()?.extras.state as FoldersResponseInner;

    if (!this.fileDetails) {
      promiseWithCatch(this.router.navigateByUrl('/files-manager'));
      return;
    }

    this.filesDetailsService.fileDetails = this.fileDetails;
    this.isFileOwner = this.fileDetails.entity.creator_account_id === this.sessionService.userId;

    this.subscribeToFileHistoryChanges();
    this.filesDetailsService.fetchFileHistory();
  }
}
